<script setup>
import { computed, onMounted, ref } from 'vue';

import { usePrefillParams } from '@/composables/useUrlParams.js';
import { regexHouseNumber, regexPostalCode } from '@/config/config.formkitValidationRules.js';
import { aanvragerStore } from '@/stores/aanvrager.js';
import { bestuurderStore } from '@/stores/bestuurder.js';
import { eigenaarStore } from '@/stores/eigenaar.js';
import { formStore } from '@/stores/form.js';
import { kentekenhouderStore } from '@/stores/kentekenhouder.js';
import { woningStore } from '@/stores/woning.js';
import { vragenStore } from '@/stores/vragen.js';
import { storeToRefs } from 'pinia';

const useFormStore = formStore();
const useAanvragerStore = aanvragerStore();
const useBestuurderStore = bestuurderStore();
const useEigenaarStore = eigenaarStore();
const useKentekenhouderStore = kentekenhouderStore();
const useWoningStore = woningStore();
const useVragenStore = vragenStore();
const { voertuig } = storeToRefs(useVragenStore);

const props = defineProps({
  nawStore: {
    type: String,
    default: null,
  },
  onlyOnSubmit: {
    type: Boolean,
    default: false,
  },
  hasHome: {
    type: Boolean,
    default: false,
  },
});

const localPostcode = ref('');
const localHuisnummer = ref('');
const localToevoeging = ref('');
const localAddress = ref('');
const localAddressNotFound = ref(false);
const useNawStore = ref({});

const toevoegingenOpties = computed(() => {
  return useFormStore.toevoegingenOpties;
});

const notFound = computed(() => {
  const { addressNotFound } = useNawStore.value;
  return addressNotFound;
});

const setNawStore = () => {
  switch (props.nawStore) {
    case 'aanvrager':
      useNawStore.value = useAanvragerStore;
      useAanvragerStore.$subscribe(() => {
        setValues();
      });
      break;
    case 'bestuurder':
      useNawStore.value = useBestuurderStore;
      useBestuurderStore.$subscribe(() => {
        setValues();
      });
      break;
    case 'eigenaar':
      useNawStore.value = useEigenaarStore;
      useEigenaarStore.$subscribe(() => {
        setValues();
      });
      break;
    case 'kentekenhouder':
      useNawStore.value = useKentekenhouderStore;
      useKentekenhouderStore.$subscribe(() => {
        setValues();
      });
      break;
    default:
      useNawStore.value = useAanvragerStore;
      useAanvragerStore.$subscribe(() => {
        setValues();
      });
  }
};

const updateRefsFromStore = () => {
  const { postcode, huisnummer, toevoeging, adres, addressNotFound } = useNawStore.value;
  localPostcode.value = typeof postcode === 'undefined' || postcode === null ? '' : postcode;
  localHuisnummer.value = typeof huisnummer === 'undefined' || huisnummer === null ? '' : huisnummer;
  localToevoeging.value = typeof toevoeging === 'undefined' || toevoeging === null ? '' : toevoeging;
  localAddress.value = typeof adres === 'undefined' || adres === null ? '' : adres;
  localAddressNotFound.value = addressNotFound;
};

const resetAddress = (all = true) => {
  useNawStore.value.straat = '';
  if (all) {
    useNawStore.value.huisnummer = '';
    useNawStore.value.toevoeging = '';
    useNawStore.value.postcode = '';
  } else {
    useNawStore.value.huisnummer = localHuisnummer.value;
    useNawStore.value.toevoeging = localToevoeging.value;
    useNawStore.value.postcode = localPostcode.value;
  }
  useNawStore.value.woonplaats = '';
  useNawStore.value.adres = '';
  useNawStore.value.addressNotFound = false;
};

const getAddressOnChange = () => {
  if (props.onlyOnSubmit) {
    resetAddress(false);
    return;
  }
  if (!regexPostalCode({ value: localPostcode.value })) {
    resetAddress(false);
    return;
  }
  if (!regexHouseNumber({ value: localHuisnummer.value })) {
    resetAddress(false);
    return;
  }
  getAddress();
};

const getAddress = async () => {
  const response = await useFormStore.fetchAddress(localPostcode.value, localHuisnummer.value, localToevoeging.value);
  resetAddress(false);
  if (response.addressNotFound) {
    useNawStore.value.addressNotFound = response.addressNotFound;
    localAddress.value = '';
  } else {
    localAddress.value = `${response.street} ${response.houseNumber}${localToevoeging.value !== null ? ' ' + localToevoeging.value : ''}<br/>${
      response.postcode
    } ${response.city}`;
    useNawStore.value.addressNotFound = response.addressNotFound;
    useNawStore.value.straat = response.street;
    useNawStore.value.huisnummer = response.houseNumber;
    useNawStore.value.toevoeging = localToevoeging.value;
    useNawStore.value.postcode = response.postcode;
    useNawStore.value.woonplaats = response.city;
    useNawStore.value.adres = localAddress.value;
    if (props.hasHome) {
      setHome(response);
    }
  }
};

const setHome = (val) => {
  useWoningStore.straat = val.street;
  useWoningStore.huisnummer = val.houseNumber;
  useWoningStore.huisnummertoevoegsel = localToevoeging.value;
  useWoningStore.postcode = val.postcode;
  useWoningStore.plaats = val.city;
  useWoningStore.type = val.type;
  useWoningStore.inhoud = val.inhoud;
  useWoningStore.woonoppervlakte = val.surfaceArea;
  useWoningStore.bouwjaar = val.bouwjaar;
  useWoningStore.herbouwwaarden = val.herbouwwaarde;
  useWoningStore.residentie = val.residency;
};

const setValues = () => {
  updateRefsFromStore();
};

/**
 * Check if the prefill params are set and if so, set the local values
 * These params will be set when the user comes from the Wordpress POST request and are handled in the useUrlParams composable
 * @returns {boolean}
 */
const checkPrefill = () => {
  // If the vehicle use is set to commercial use and the nawStore is set to 'aanvrager', we don't want to use the prefilled params (from the Wordpress POST request)
  // This needs to be overwritten by the specific business where we retrieved the data from the KVK API earlier
  if (voertuig.value.Gebruik === 'Zakelijk' && props.nawStore === 'aanvrager') {
    return false;
  }

  const { newPostcode, newHuisnummer, newToevoeging } = usePrefillParams();
  if (newPostcode && newHuisnummer) {
    if (newPostcode) localPostcode.value = newPostcode;
    if (newHuisnummer) localHuisnummer.value = newHuisnummer;
    if (newToevoeging) localToevoeging.value = newToevoeging;
    getAddress();
    return true;
  } else {
    return false;
  }
};

/**
 * On component mount
 */
onMounted(() => {
  setNawStore();
  // Check if we need to use the prefill params, if so, we do check the postal code again and set the values in the getAddress function
  // If not, we update the refs directly from the store
  if (!checkPrefill()) {
    updateRefsFromStore();
  }
});

defineExpose({
  getAddress,
});
</script>

<template>
  <div class="row">
    <div class="col">
      <BaseInput
        v-model="localPostcode"
        :name="`${nawStore}Postcode`"
        :label="$t('start-calculation.addressPostcode.postalCode')"
        :placeholder="$t('start-calculation.addressPostcode.postalCodePlaceHolder')"
        :validation="'required|regexPostalCode'"
        uppercase
        delay="1000"
        @keydown.space.prevent
        @input="
          (val) => {
            if (localPostcode === val) return;
            localPostcode = val;
            getAddressOnChange();
          }
        "
      />
    </div>
  </div>

  <!-- House number -->
  <div class="row mw-425">
    <div class="col-8">
      <BaseInput
        v-model="localHuisnummer"
        type="number"
        :name="`${nawStore}HouseNumber`"
        :label="$t('start-calculation.addressPostcode.houseNumber')"
        :placeholder="$t('start-calculation.addressPostcode.houseNumber')"
        :validation="'required|regexHouseNumber'"
        delay="1000"
        @input="
          (val) => {
            if (localHuisnummer === val) return;
            localHuisnummer = val;
            getAddressOnChange();
          }
        "
      />
    </div>

    <!-- If only on submit then we do not check the House number addition and the input can be anything -->
    <div
      v-if="onlyOnSubmit"
      class="col-4"
    >
      <BaseInput
        v-model="localToevoeging"
        :name="`${nawStore}Toevoeging`"
        :label="$t('start-calculation.addressPostcode.houseNumberAdditionPlaceHolder')"
        :placeholder="$t('start-calculation.addressPostcode.houseNumberAdditionPlaceHolder')"
        delay="1000"
        @input="
          (val) => {
            if (localToevoeging === val) return;
            localToevoeging = val;
            getAddressOnChange();
          }
        "
      />
    </div>

    <!-- Else we first collect the input options to select the exact House number addition -->
    <div
      v-else
      class="col-4"
    >
      <BaseDropDown
        v-model="localToevoeging"
        :name="`${nawStore}Toevoeging`"
        :label="$t('start-calculation.addressPostcode.houseNumberAdditionPlaceHolder')"
        :placeholder="$t('start-calculation.addressPostcode.houseNumberAdditionPlaceHolder')"
        :options="toevoegingenOpties"
        delay="1000"
        @input="
          (val) => {
            if (localToevoeging === val) return;
            localToevoeging = val;
            getAddressOnChange();
          }
        "
      />
    </div>
  </div>

  <div
    v-if="localAddress"
    class="row"
  >
    <div class="col-12">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div
        data-wg-notranslate
        class="card p-4 mb-4"
        v-html="`<strong>${localAddress}</strong>`"
      />
    </div>
  </div>

  <!-- Invalid address message -->
  <p
    v-if="notFound"
    class="text-danger"
  >
    {{ $t('start-calculation.addressPostcode.noAddressFound') }}
  </p>
</template>

<style scoped lang="scss">
.card {
  --bs-border-radius: 1rem;
  background-color: var(--ux-colors-black-05);
}
</style>
